import { Hint } from '../../components/ui/hint';
import { DateField, localExpiry } from '../../components/ui/date-field';
import { CreditShadowPanel } from './credit-shadow';
import { CreditChargingPanel } from './credit-charging';
import { DeliveryReadiness } from './delivery-readiness';
import { useEffect, useRef, useState } from 'react';
import { api, errorLine } from '../../api/client';
import type { CreditChange, CreditReceipt, CreditView } from '../../api/types';
import {
  clearCreditChange,
  creditUnits,
  definitelyRejected,
  formatCredits,
  keepCreditChange,
  pendingCreditChange,
} from '../../lib/credits';

export function Credits({ orgId }: { orgId: string }) {
  const [view, setView] = useState<CreditView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<CreditChange | null>(null);
  const [storageReady, setStorageReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const inFlight = useRef(false);
  const reading = useRef(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [kind, setKind] = useState<CreditChange['kind']>('grant');
  const [target, setTarget] = useState<CreditReceipt | null>(null);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [reference, setReference] = useState('');
  const [expiry, setExpiry] = useState('');
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    try {
      setPending(pendingCreditChange(sessionStorage, orgId));
      setStorageReady(true);
    } catch {
      setError(
        'The saved credit request could not be read. Resolve browser storage before submitting another change.',
      );
    }
    api
      .credits(orgId)
      .then((v) => {
        if (live) setView(v);
      })
      .catch((e: unknown) => {
        if (live) setError(errorLine(e));
      });
    return () => {
      live = false;
    };
  }, [orgId]);

  async function refresh() {
    if (reading.current) return;
    reading.current = true;
    setLoadingHistory(true);
    try {
      setView(await api.credits(orgId));
    } catch (e) {
      setError(`Could not refresh credit history. ${errorLine(e)}`);
    } finally {
      reading.current = false;
      setLoadingHistory(false);
    }
  }
  async function more() {
    if (!view?.next_before || reading.current) return;
    reading.current = true;
    setLoadingHistory(true);
    try {
      const next = await api.credits(orgId, view.next_before);
      setView((old) =>
        old
          ? {
              ...next,
              entries: [...old.entries, ...next.entries],
              grants: [...old.grants, ...next.grants],
            }
          : next,
      );
    } catch (e) {
      setError(errorLine(e));
    } finally {
      reading.current = false;
      setLoadingHistory(false);
    }
  }
  async function submit() {
    if (inFlight.current || reading.current || !storageReady) return;
    inFlight.current = true;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      let change = pending;
      if (!change) {
        const units = creditUnits(amount);
        if (!units || !reason.trim())
          throw new Error('Enter a positive credit amount and a reason.');
        if (kind === 'grant' && expiry && !localExpiry(expiry))
          throw new Error('Choose a valid expiry date and time.');
        const common = { command_key: crypto.randomUUID(), units, reason: reason.trim() };
        if (kind === 'grant')
          change = {
            ...common,
            kind,
            reference: reference.trim() || null,
            expires_at: expiry ? localExpiry(expiry) : null,
          };
        else if (kind === 'revoke' && target) change = { ...common, kind, grant_id: target.id };
        else if (kind === 'refund' && target?.hold_id)
          change = { ...common, kind, hold_id: target.hold_id };
        else throw new Error('Choose an allocation or settled operation from history.');
        keepCreditChange(sessionStorage, orgId, change);
        setPending(change);
      }
      try {
        const receipt = await api.changeCredits(orgId, change);
        clearCreditChange(sessionStorage, orgId);
        setPending(null);
        setNotice(`Saved credit change #${receipt.sequence}.`);
        setAmount('');
        setReason('');
        setReference('');
        setExpiry('');
        setKind('grant');
        setTarget(null);
        await refresh();
      } catch (e) {
        if (definitelyRejected(e)) {
          clearCreditChange(sessionStorage, orgId);
          setPending(null);
        }
        setError(errorLine(e));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save this request in browser storage.');
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  }
  function choose(next: CreditChange['kind'], entry: CreditReceipt | null) {
    setKind(next);
    setTarget(entry);
    setAmount('');
    setReason('');
    setNotice(null);
  }
  return (
    <div className="section credit-ledger">
      <header className="credit-page-head">
        <div>
          <div className="eyebrow">Account management</div>
          <h2>Pricing &amp; credits</h2>
        </div>
        <span className="credit-badge">
          {view?.charging_enabled ? 'Charging is on' : 'Charging is off'}
        </span>
      </header>
      <CreditChargingPanel
        key={`charging-${orgId}`}
        orgId={orgId}
        onChange={() => void refresh()}
      />
      <details className="credit-admin-tools" open={pending ? true : undefined}>
        <summary>Account controls and diagnostics</summary>
        {view ? <CreditSummary view={view} /> : <p>Loading credit account…</p>}
        {view && !view.reconciled ? (
          <p role="alert">
            The account does not match its journal. Investigate before making changes.
          </p>
        ) : null}
        <section className="credit-card" aria-label="Manual credit allocation">
          {pending ? (
            <div className="flag">
              <p>
                A {pending.kind} request for {formatCredits(pending.units)} credits is awaiting
                confirmation. Retry this same request to recover its receipt.
              </p>
              <p>{pending.reason}</p>
            </div>
          ) : (
            <>
              <h3>
                {kind === 'grant'
                  ? 'Grant credits'
                  : kind === 'revoke'
                    ? 'Remove unused credits'
                    : 'Refund an operation'}
              </h3>
              {target ? (
                <p>
                  For change #{target.sequence}: {target.reason}{' '}
                  <button
                    type="button"
                    className="btn"
                    disabled={saving}
                    onClick={() => choose('grant', null)}
                  >
                    Back to grants
                  </button>
                </p>
              ) : null}

              <div className="credit-form-grid">
                <div className="field">
                  <label className="label" htmlFor="credit-amount">
                    Credits
                  </label>
                  <input
                    id="credit-amount"
                    type="text"
                    inputMode="decimal"
                    value={amount}
                    disabled={saving}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                  <Hint label="Credit precision">Up to three decimal places.</Hint>
                </div>
                <div className="field">
                  <label className="label" htmlFor="credit-reason">
                    Reason
                  </label>
                  <input
                    placeholder="For example: Initial pilot allocation"
                    id="credit-reason"
                    type="text"
                    maxLength={1000}
                    value={reason}
                    disabled={saving}
                    onChange={(e) => setReason(e.target.value)}
                  />
                  <Hint label="About the credit reason">
                    Internal audit note explaining this change.
                  </Hint>
                </div>
                {kind === 'grant' ? (
                  <>
                    <div className="field">
                      <label className="label" htmlFor="credit-reference">
                        Allocation reference (optional)
                      </label>
                      <input
                        id="credit-reference"
                        type="text"
                        maxLength={200}
                        placeholder="For example: September 2026 allocation"
                        value={reference}
                        disabled={saving}
                        onChange={(e) => setReference(e.target.value)}
                      />
                      <Hint label="About allocation references">
                        A unique reference prevents granting the same allocation twice.
                      </Hint>
                    </div>
                    <DateField
                      id="credit-expiry"
                      label="Expiry · optional, local time"
                      mode="datetime"
                      value={expiry}
                      disabled={saving}
                      onChange={setExpiry}
                    />
                  </>
                ) : null}
              </div>
              {kind === 'refund' ? (
                <p>
                  Refunded credits retain the original allocation expiry. Already-expired credits
                  will not become available.
                </p>
              ) : null}
            </>
          )}
          {error ? <p role="alert">{error}</p> : null}
          {notice ? <p role="status">{notice}</p> : null}
          <div className="form-actions">
            <button
              type="button"
              className="btn go"
              disabled={
                saving ||
                loadingHistory ||
                !storageReady ||
                !view?.reconciled ||
                (!pending && (!creditUnits(amount) || !reason.trim()))
              }
              onClick={() => void submit()}
            >
              {saving
                ? 'Saving…'
                : pending
                  ? 'Retry saved request'
                  : kind === 'grant'
                    ? 'Grant credits'
                    : kind === 'revoke'
                      ? 'Remove credits'
                      : 'Refund credits'}
            </button>
            <button
              type="button"
              className="btn"
              disabled={saving || loadingHistory}
              onClick={() => void refresh()}
            >
              Refresh
            </button>
          </div>
        </section>
        <section className="credit-card">
          <h3>Credit journal</h3>
          <Hint label="About credit history">
            Newest first. Corrections add entries without rewriting history.
          </Hint>
          {view?.entries.length === 0 ? <p>No credits have been granted.</p> : null}
          <div className="credit-history">
            <table className="table">
              <thead>
                <tr>
                  <th>Change</th>
                  <th>Reason / allocation</th>
                  <th>Available change</th>
                  <th>Balance after</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {view?.entries.map((e) => {
                  const g = view.grants.find((g) => g.id === e.id);
                  return (
                    <tr key={e.id}>
                      <td>
                        #{e.sequence} {e.kind}
                        <div>{new Date(e.created_at).toLocaleString()}</div>
                        <details className="row-detail">
                          <summary>{e.actor_user_id ? 'Admin change' : 'System change'}</summary>
                          {e.actor_user_id}
                        </details>
                      </td>
                      <td>
                        {e.reason}
                        {g ? (
                          <>
                            <div>{g.reference}</div>
                            <div>
                              {g.expires_at
                                ? `Expires ${new Date(g.expires_at).toLocaleString()}`
                                : 'No expiry'}
                            </div>
                            <div>{formatCredits(g.available)} unused</div>
                          </>
                        ) : null}
                      </td>
                      <td>
                        {e.delta.available > 0 ? '+' : ''}
                        {formatCredits(e.delta.available)}
                      </td>
                      <td>
                        {formatCredits(e.balance_after.available)} available
                        <div>{formatCredits(e.balance_after.reserved)} reserved</div>
                        <div>{formatCredits(e.balance_after.consumed)} consumed</div>
                      </td>
                      <td>
                        {g && g.available > 0 ? (
                          <button
                            type="button"
                            className="btn"
                            disabled={saving || !!pending}
                            onClick={() => choose('revoke', e)}
                          >
                            Remove unused
                          </button>
                        ) : null}
                        {e.kind === 'settle' && e.delta.consumed > 0 ? (
                          <button
                            type="button"
                            className="btn"
                            disabled={saving || !!pending}
                            onClick={() => choose('refund', e)}
                          >
                            Refund
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {view?.next_before ? (
            <button
              type="button"
              className="btn"
              disabled={saving || loadingHistory}
              onClick={() => void more()}
            >
              Older changes
            </button>
          ) : null}
        </section>
        <DeliveryReadiness key={`delivery-${orgId}`} orgId={orgId} />
        <CreditShadowPanel key={orgId} orgId={orgId} />
      </details>
    </div>
  );
}
export function CreditSummary({ view }: { view: CreditView }) {
  return (
    <div className="credit-balances">
      <div>
        <span>Available credits</span>
        <strong>{formatCredits(view.balance.available)}</strong>
      </div>
      <div>
        <span>Reserved credits</span>
        <strong>{formatCredits(view.balance.reserved)}</strong>
      </div>
      <div>
        <span>Consumed credits</span>
        <strong>{formatCredits(view.balance.consumed)}</strong>
        <small>net of refunds</small>
      </div>
    </div>
  );
}
